import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useLang } from "@/contexts/AppContexts";
import { Trash2, Eye, History as HistoryIcon, Inbox, ShieldCheck, AlertTriangle, HelpCircle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;
const ALLOW_HISTORY_MUTATION =
  process.env.REACT_APP_ALLOW_HISTORY_MUTATION === "true";

const verdictBadgeClass = (v) => {
  switch (v) {
    case "REAL":
      return "bg-emerald-500/10 text-emerald-700 dark:text-emerald-200 border-emerald-500/30";
    case "AI":
      return "bg-red-500/10 text-red-700 dark:text-red-200 border-red-500/30";
    default:
      return "bg-amber-500/10 text-amber-800 dark:text-amber-200 border-amber-500/30";
  }
};

const VerdictIcon = ({ verdict, className }) => {
  if (verdict === "REAL") return <ShieldCheck className={className} />;
  if (verdict === "AI") return <AlertTriangle className={className} />;
  return <HelpCircle className={className} />;
};

const formatDate = (s) => {
  try {
    const d = new Date(s);
    return d.toLocaleString();
  } catch {
    return s;
  }
};

const HistoryPage = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/history`);
      setItems(res.data.items || []);
    } catch (e) {
      toast.error(t("errors.generic"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/history/${id}`);
      toast.success(t("history.deleted"));
      setItems((prev) => prev.filter((it) => it.id !== id));
    } catch {
      toast.error(t("errors.generic"));
    }
  };

  const handleClearAll = async () => {
    try {
      await axios.delete(`${API}/history`);
      toast.success(t("history.cleared"));
      setItems([]);
    } catch {
      toast.error(t("errors.generic"));
    }
  };

  return (
    <div className="app-shell">
      <Header />
      <main className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8 py-6 lg:py-10 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-[11px] font-medium text-primary font-mono-plex tracking-wider uppercase">
              <HistoryIcon className="h-3 w-3" />
              {t("nav.history")}
            </span>
            <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight">
              {t("history.title")}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("history.subtitle")}
            </p>
          </div>
          {ALLOW_HISTORY_MUTATION && items.length > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" data-testid="history-clear-all-button">
                  <Trash2 className="h-4 w-4 mr-1.5" />
                  {t("history.clear_all")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("history.clear_all")}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t("history.clear_confirm")}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleClearAll}
                    data-testid="history-clear-all-confirm"
                  >
                    {t("common.yes")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-56 rounded-xl shimmer-loader" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <Card
            className="py-16 px-6 text-center flex flex-col items-center gap-3"
            data-testid="history-empty"
          >
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-muted text-muted-foreground">
              <Inbox className="h-6 w-6" />
            </div>
            <div>
              <p className="font-display text-lg font-semibold">
                {t("history.empty_title")}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {t("history.empty_subtitle")}
              </p>
            </div>
            <Link to="/">
              <Button>{t("history.go_analyze")}</Button>
            </Link>
          </Card>
        ) : (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
            data-testid="history-grid"
          >
            {items.map((it) => (
              <Card
                key={it.id}
                className="group relative overflow-hidden flex flex-col cursor-pointer hover:shadow-lg transition-shadow"
                data-testid="history-grid-item"
                onClick={() => navigate(`/history/${it.id}`)}
              >
                <div className="relative aspect-[4/3] bg-muted overflow-hidden">
                  {it.thumb_b64 ? (
                    <img
                      src={`data:image/jpeg;base64,${it.thumb_b64}`}
                      alt={it.filename}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full text-muted-foreground">
                      <Eye className="h-6 w-6" />
                    </div>
                  )}
                  <div className="absolute top-2 left-2">
                    <Badge
                      variant="outline"
                      className={`${verdictBadgeClass(
                        it.verdict
                      )} backdrop-blur shadow-sm`}
                    >
                      <VerdictIcon
                        verdict={it.verdict}
                        className="h-3 w-3 mr-1"
                      />
                      {t(`verdict.${it.verdict}`)}
                    </Badge>
                  </div>
                  <div className="absolute top-2 right-2 rounded-md bg-background/80 backdrop-blur px-2 py-0.5 text-[10px] font-mono-plex font-semibold tabular-nums">
                    {it.confidence}%
                  </div>
                </div>
                <div className="p-3 flex flex-col gap-1.5">
                  <p
                    className="text-sm font-medium truncate"
                    title={it.filename}
                  >
                    {it.filename}
                  </p>
                  <p className="text-[11px] text-muted-foreground font-mono-plex">
                    {formatDate(it.started_at)}
                  </p>
                  <div className="flex items-center justify-between gap-2 pt-1">
                    <span className="text-[10px] text-muted-foreground">
                      {t("history.duration")}{" "}
                      {it.duration_ms != null
                        ? `${(it.duration_ms / 1000).toFixed(1)}s`
                        : "—"}
                    </span>
                    {ALLOW_HISTORY_MUTATION && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(it.id);
                        }}
                        aria-label={t("history.delete")}
                        data-testid="history-delete-button"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default HistoryPage;
